import { z } from 'zod';
import { REGIONS, type Region } from '@/engine/hands';
import { NO_BLOCKERS, ONE_BLOCKER, type Blockers } from '@/engine/combos';
import { levels } from './levels.config';

/** A group of modes that share a twist, shown as one card on Home that opens its own mode cards. */
export const GroupDefSchema = z.object({
  id: z.string().min(1),
  /** lines of the combo grid blocked by our cards (see `engine/combos`); 'custom' = the player picks them (Card Blockers) */
  blockers: z.union([
    z.object({ rows: z.number().int().min(0).max(3), cols: z.number().int().min(0).max(3) }),
    z.literal('custom'),
  ]),
});
export type GroupDef = z.infer<typeof GroupDefSchema>;

export const groups: readonly GroupDef[] = z.array(GroupDefSchema).parse([
  { id: 'plain', blockers: NO_BLOCKERS },
  { id: 'blocker', blockers: ONE_BLOCKER },
  { id: 'cards', blockers: 'custom' },
]);
export type GroupId = (typeof groups)[number]['id'];

/**
 * A mode fixes which part of the matrix is in play and the B constraints.
 * Levels (1–3 terms) apply to every mode; each mode has its own difficulty caps for B.
 * A mode inside a group inherits the group's blockers; every mode sits in a group.
 */
export const ModeDefSchema = z.object({
  id: z.string().min(1),
  region: z.enum(Object.keys(REGIONS) as [Region, ...Region[]]),
  b: z.object({ min: z.number().int().min(1), max: z.number().int() }),
  bSumMax: z.number().int().positive(),
  caps: z.array(z.number().int().positive()).min(1),
  group: z.enum(groups.map((g) => g.id) as [GroupId, ...GroupId[]]).optional(),
});
export type ModeDef = z.infer<typeof ModeDefSchema>;

function defineModes(defs: ModeDef[]): readonly ModeDef[] {
  const parsed = z.array(ModeDefSchema).parse(defs);
  const maxTerms = Math.max(...levels.map((l) => l.terms));
  for (const m of parsed) {
    const size = REGIONS[m.region].length;
    if (m.b.max > size || m.bSumMax > size)
      throw new Error(`Mode "${m.id}": B exceeds the ${size}-hand region`);
    if (maxTerms * m.b.min > m.bSumMax)
      throw new Error(`Mode "${m.id}": ${maxTerms} terms × b.min exceeds bSumMax`);
    for (const cap of m.caps)
      if (cap < m.b.min || cap > m.b.max)
        throw new Error(`Mode "${m.id}": cap ${cap} outside B range`);
  }
  return parsed;
}

const range = (): Omit<ModeDef, 'id'> => ({
  region: 'full',
  b: { min: 2, max: 78 },
  bSumMax: 78,
  caps: [10, 25, 50, 78],
});
const stripe = (): Omit<ModeDef, 'id'> => ({
  region: 'stripe',
  b: { min: 2, max: 12 },
  bSumMax: 12,
  caps: [12],
});

export const modes = defineModes([
  { id: 'range', ...range(), group: 'plain' },
  { id: 'stripe', ...stripe(), group: 'plain' },
  { id: 'blocker-range', ...range(), group: 'blocker' },
  { id: 'blocker-stripe', ...stripe(), group: 'blocker' },
  { id: 'cards-range', ...range(), group: 'cards' },
  { id: 'cards-stripe', ...stripe(), group: 'cards' },
]);

export type ModeId = (typeof modes)[number]['id'];

/** Blockers a mode plays with: its group's, the player's own choice for a 'custom' group, or none. */
export function blockersOf(mode: ModeDef, custom: Blockers): Blockers {
  const b = groups.find((g) => g.id === mode.group)?.blockers ?? NO_BLOCKERS;
  return b === 'custom' ? custom : b;
}
/** Top-level Home entries: modes without a group. */
export const topModes = modes.filter((m) => !m.group);
export const modesInGroup = (groupId: string): readonly ModeDef[] =>
  modes.filter((m) => m.group === groupId);
